// Cliente API del módulo Diagnósticos — Django /api/v1/diagnostics (mismo patrón que focus/api.ts).
// La org la pone el servidor desde el JWT; aquí nunca se manda org_id.
// ADR-007: si la consultora eligió una empresa (selector «Empresa»), TODAS las
// llamadas del módulo agregan `X-Acting-Org`; el backend lo acepta solo con un
// engagement activo. Un 403 con ese header trae el motivo en español (sin acceso,
// revocado/pausado, vencido, permiso no otorgado): se muestra y se vuelve a mi org.
// Programas/embudo NO se delegan (el backend da 403 con el header): esas llamadas
// van SIEMPRE sin X-Acting-Org (ownOrg).
import { ACTING_ORG_HEADER, actingOrgHeaders, clearActingOrg } from '@/lib/actingOrg';
import type {
  DiagClient, DiagProgram, DiagProject, Funnel, InvitationPreview, ParticipantDetail, ParticipantResult,
  ProjectReport, EffectiveView,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/diagnostics';

/** Scope mínimo para entrar a una empresa desde este módulo. */
export const DIAG_SCOPE = 'diagnostics:read';

/** Rutas que nunca se delegan a otra empresa (routers_programs: reject_acting_org). */
const OWN_ORG_ONLY = /^\/programs(\/|\?|$)/;

function headers(json = false, ownOrg = false): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    Authorization: `Bearer ${token ?? ''}`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(ownOrg ? {} : actingOrgHeaders()),
  };
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function asJson<T>(res: Response, acting = false): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const b = await res.json();
      msg = typeof b.detail === 'string' ? b.detail : JSON.stringify(b.detail ?? b);
    } catch {
      /* noop */
    }
    // 403 trabajando en otra empresa → el motivo del servidor y de vuelta a mi org.
    if (res.status === 403 && acting) clearActingOrg(`${msg} Volviste a tu organización.`);
    throw new ApiError(res.status, msg);
  }
  return res.json() as Promise<T>;
}

const url = (path: string) => `${DJANGO_PROXY}${BASE}${path}`;
const isActing = (h: Record<string, string>) => ACTING_ORG_HEADER in h;
const get = async <T,>(path: string) => {
  const h = headers(false, OWN_ORG_ONLY.test(path));
  return asJson<T>(await fetch(url(path), { headers: h }), isActing(h));
};
const send = async <T,>(method: string, path: string, body?: unknown) => {
  const h = headers(body !== undefined, OWN_ORG_ONLY.test(path));
  return asJson<T>(await fetch(url(path), {
    method,
    headers: h,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }), isActing(h));
};

const qs = (params: Record<string, string | boolean | undefined>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== false) p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

// ── Programas ──
export const listPrograms = (incluirArchivados = false) =>
  get<{ programs: DiagProgram[] }>(`/programs${qs({ incluir_archivados: incluirArchivados })}`);
export const getProgram = (id: string) => get<DiagProgram>(`/programs/${id}`);
export const createProgram = (body: Record<string, unknown>) => send<DiagProgram>('POST', '/programs', body);
export const updateProgram = (id: string, body: Record<string, unknown>) => send<DiagProgram>('PATCH', `/programs/${id}`, body);
export const archiveProgram = (id: string) => send<{ ok: boolean }>('DELETE', `/programs/${id}`);
export const getFunnel = (id: string) => get<Funnel>(`/programs/${id}/funnel`);

// ── Proyectos ──
export const listClients = () => get<{ clients: DiagClient[] }>('/clients');
export const listProjects = (f: { program_id?: string; client_id?: string; status?: string; sin_programa?: boolean }) =>
  get<{ projects: DiagProject[] }>(`/projects${qs(f)}`);
export const getProject = (id: string) => get<DiagProject>(`/projects/${id}`);
export const catalogo = () => get<unknown>('/catalogo');
export const createProject = (body: Record<string, unknown>) => send<DiagProject>('POST', '/projects', body);
export const updateProject = (id: string, body: Record<string, unknown>) => send<DiagProject>('PATCH', `/projects/${id}`, body);
export const projectConfig = (id: string) => get<EffectiveView>(`/projects/${id}/config`);
export const followUp = (id: string) => send<DiagProject>('POST', `/projects/${id}/follow-up`, {});

// ── Participantes ──
export const addParticipants = (id: string, raw: string, isTest = false) =>
  send<{ created: unknown[]; duplicates: string[]; invalid: string[]; ignored_lines: string[] }>(
    'POST', `/projects/${id}/participants`, { raw, is_test: isTest });
export const removeParticipant = (id: string, pid: string) => send<{ ok: boolean }>('DELETE', `/projects/${id}/participants/${pid}`);
export const getParticipant = (id: string, pid: string) => get<ParticipantDetail>(`/projects/${id}/participants/${pid}`);
export const analyzeParticipant = (id: string, pid: string) => send<{ status: string }>('POST', `/projects/${id}/participants/${pid}/analyze`, {});
export const previewInvitations = (id: string, ids: string[] | null) =>
  send<{ dry_run: true; count: number; sender_org: string; emails: InvitationPreview[] }>(
    'POST', `/projects/${id}/invitations`, { participant_ids: ids, dry_run: true });
export const sendInvitations = (id: string, ids: string[] | null) =>
  send<{ dry_run: false; count: number; sent: number; results: { participant_id: string; sent: boolean; error: string }[] }>(
    'POST', `/projects/${id}/invitations`, { participant_ids: ids, dry_run: false });

// ── Reporte agregado ──
export const generateReport = (id: string) => send<{ report_id: string; status: string }>('POST', `/projects/${id}/report`, {});
export const getReport = (id: string) => get<ProjectReport>(`/projects/${id}/report`);

// ── Resultado del dueño ──
export const getResult = (pid: string) => get<ParticipantResult>(`/participants/${pid}/result`);
export const draftResult = (pid: string, result?: Record<string, unknown>) =>
  send<ParticipantResult>('POST', `/participants/${pid}/result/draft`, result ? { result } : {});
export const releaseResult = (pid: string) => send<ParticipantResult>('POST', `/participants/${pid}/result/release`, { notify: true });
